import React from 'react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';

/**
 * AnswerBox — "Quick answer" block.
 *
 * Renders a short, direct answer at the very top of the page in a visually
 * distinct card and mirrors it into a QAPage JSON-LD block for structured data.
 *
 * Design language:
 *   - Ivory canvas with a gold rail on the left (matches ScholarlyPullQuote)
 *   - "Quick Answer" mono uppercase eyebrow chip with a sparkle icon
 *   - 1-line question, 2–4 line answer, optional "How it works" bullets
 *
 * Props:
 *   question   – string (headline)
 *   answer     – React node (the direct answer, 2–4 lines)
 *   points     – optional array of {label, value} — mini "steps" list
 *   testid     – base data-testid (default "answer-box")
 */
export const AnswerBox = ({
  question,
  answer,
  points,
  testid = 'answer-box',
  // Compact mode: renders the TL;DR collapsed by default with question +
  // small eyebrow visible; user taps to expand answer + steps. Used on
  // Inheritance and Will pages where the wizard is the visual centre.
  compact = false,
}) => {
  const { t } = useZakatGlobal();
  const tt = (k, fallback) => {
    const v = t ? t(k) : null;
    return (v && v !== k) ? v : fallback;
  };
  // Emit a minimal QAPage JSON-LD structured data block for search results.
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: typeof answer === 'string' ? answer : String(answer),
      },
    },
  };

  return (
    <aside
      data-testid={testid}
      className={`relative isolate overflow-hidden rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 to-[#F3EFEA] shadow-[0_4px_24px_-16px_rgba(6,78,59,0.15)] ${compact ? 'p-3 md:p-4' : 'p-6 md:p-7'}`}
    >
      {/* Gold left rail — mirror of ScholarlyPullQuote for visual family */}
      <div aria-hidden className="absolute left-0 top-0 bottom-0 w-[3px] bg-gold-500/70" />

      {compact ? (
        <details data-testid={`${testid}-details`} className="group">
          <summary className="list-none cursor-pointer flex items-center gap-2 select-none">
            <span
              data-testid={`${testid}-eyebrow`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EDF5F1] text-[#0D4733] px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-[0.22em]"
            >
              <Sparkles className="w-2.5 h-2.5" />
              {tt('common.quickAnswer', 'Quick Answer')}
            </span>
            <span
              data-testid={`${testid}-question`}
              className="font-heading text-[13.5px] md:text-[14.5px] leading-snug text-[#0D1520] flex-1 min-w-0"
            >
              {question}
            </span>
            <ChevronRight className="w-4 h-4 text-[#4A5568] shrink-0 group-open:rotate-90 transition-transform" />
          </summary>
          <div className="mt-3">
            <div
              data-testid={`${testid}-answer`}
              className="text-[13px] md:text-[14px] leading-relaxed text-stone-800"
            >
              {answer}
            </div>
            {Array.isArray(points) && points.length > 0 && (
              <ol
                data-testid={`${testid}-steps`}
                className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5 text-[12px] md:text-[12.5px] text-stone-700"
              >
                {points.map((p, i) => (
                  <li key={`row-${i}`} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EDF5F1] text-[#0D4733] text-[9px] font-mono shrink-0">
                      {i + 1}
                    </span>
                    <span>
                      <span className="font-semibold text-[#0D1520]">{p.label}</span>
                      {p.value && (
                        <>
                          <ChevronRight aria-hidden className="inline w-3 h-3 mx-0.5 text-stone-400 rtl:rotate-180" />
                          {p.value}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </details>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <span
              data-testid={`${testid}-eyebrow`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EDF5F1] text-[#0D4733] px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.22em]"
            >
              <Sparkles className="w-3 h-3" />
              {tt('common.quickAnswer', 'Quick Answer')}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-stone-500">
              {tt('common.directSummary', 'Direct summary')}
            </span>
          </div>

          <h2
            data-testid={`${testid}-question`}
            className="font-heading text-[19px] md:text-[22px] leading-snug text-[#0D1520] mb-3"
          >
            {question}
          </h2>

          <div
            data-testid={`${testid}-answer`}
            className="text-[15px] md:text-[16px] leading-relaxed text-stone-800"
          >
            {answer}
          </div>

          {Array.isArray(points) && points.length > 0 && (
            <ol
              data-testid={`${testid}-steps`}
              className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[13px] md:text-[14px] text-stone-700"
            >
              {points.map((p, i) => (
                <li key={`row-${i}`} className="flex items-start gap-2">
                  <span className="mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EDF5F1] text-[#0D4733] text-[9px] font-mono shrink-0">
                    {i + 1}
                  </span>
                  <span>
                    <span className="font-semibold text-[#0D1520]">{p.label}</span>
                    {p.value && (
                      <>
                        <ChevronRight aria-hidden className="inline w-3 h-3 mx-0.5 text-stone-400 rtl:rotate-180" />
                        {p.value}
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </aside>
  );
};

export default AnswerBox;
